import React from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Amplify, Analytics } from 'aws-amplify';
import 'bootstrap/dist/css/bootstrap.min.css';
import AdminConsole from './AdminConsole/AdminConsole';
import CreateTemplate from './AdminConsole/CreateTemplate/CreateTemplate';
import EditTemplateDefinition from './AdminConsole/EditTemplateDefinition/EditTemplateDefinition';
import App from './App';
import './App.css';
import DownloadHistoryConsole from './DataDownloadHistoryConsole/DownloadHistoryConsole';
import AllDataRequests from './DataRequests/AllDataRequests/AllDataRequests';
import DataRequestSubmission from './DataRequests/DataRequestSubmission/DataRequestSubmission';
import MyDataRequests from './DataRequests/MyDataRequests/MyDataRequests';
import ExploreCompensationData from './ExploreCompensationDataPage/ExploreCompensationData';
import GroupVisibleComponent from './GroupVisibleComponent';
import SurveyDataConsole from './SurveyDataConsole/SurveyDataConsole';
import SurveyParticipantConsole from './SurveyParticipantConsole/SurveyParticipantConsole';
import SurveyQuestionnaireDetailsConsole from './SurveyQuestionnaireDetailsConsole/SurveyQuestionnaireDetailsConsole';
import { questionnaireDetailsLoader } from './SurveyQuestionnaireDetailsConsole/questionnaireDetailsLoader';
import SurveyQuestionnaireTrackingConsole from './SurveyQuestionnaireTrackingConsole/SurveyQuestionnaireTrackingConsole';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './components/NotFound';
import './index.scss';
import * as serviceWorker from './serviceWorker';
import { USER_GROUPS } from './utils/AuthUtils';
import OptionsData from './OptionsData/OptionsData';
import Top5DashboardIndexDataConsole from './Top5DashboardDataConsole/Top5DashboardIndexDataConsole';
import Top5DashboardPeerDataConsole from './Top5DashboardDataConsole/Top5DashboardPeerDataConsole';
import SurveyOutputDashboardConsole from './SurveyOutputDashboardConsole/SurveyOutputDashboardConsole';
import SurveyRegressionDashboardConsole from './SurveyRegressionDashboardConsole/SurveyRegressionDashboardConsole';

const domNode = document.getElementById('root');
const root = createRoot(domNode);

const config = {
  API: {
    endpoints: [
      {
        name: "AnnualCompensations",
        endpoint: process.env.REACT_APP_ANNUAL_COMPENSATIONS_API,
        region: process.env.REACT_APP_ANNUAL_COMPENSATIONS_API_REGION
      },
      {
        name: "UploadCompensations",
        endpoint: process.env.REACT_APP_COMPENSATIONS_UPLOAD_API,
        region: process.env.REACT_APP_COMPENSATIONS_UPLOAD_API_REGION
      },
      {
        name: "UnrestrictedSurveyData",
        endpoint: process.env.REACT_APP_UNRESTRICTED_SURVEY_DATA_API,
        region: process.env.REACT_APP_UNRESTRICTED_SURVEY_DATA_API_REGION
      }
    ]
  },
  Auth: {
    identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
    region: process.env.REACT_APP_COGNITO_REGION,
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_WEB_CLIENT_ID
  },
  Storage: {
    AWSS3: {
      bucket: process.env.REACT_APP_S3_BUCKET_NAME,
      region: process.env.REACT_APP_S3_BUCKET_REGION,
      identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID
    }
  }
};
Amplify.configure(config);

const analyticsConfig = {
  AWSPinpoint: {
    appId: process.env.REACT_APP_PINPOINT_APP_ID,
    region: process.env.REACT_APP_COGNITO_REGION,
    mandatorySignIn: false,
  }
};

Analytics.configure(analyticsConfig);

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "",
        element: <ExploreCompensationData />
      },
      {
        path: "admin/:tabName",
        element: (
          <GroupVisibleComponent groups={[USER_GROUPS.ADMIN, USER_GROUPS.SALES]}>
            <AdminConsole />
          </GroupVisibleComponent>
        )
      },
      {
        path: "admin/templates/new",
        element: (
          <GroupVisibleComponent groups={[USER_GROUPS.ADMIN]}>
            <CreateTemplate />
          </GroupVisibleComponent>
        )
      },
      {
        path: "admin/templates/:templateId",
        element: (
          <GroupVisibleComponent groups={[USER_GROUPS.ADMIN]}>
            <EditTemplateDefinition />
          </GroupVisibleComponent>
        )
      },
      {
        path: "dashboard/exec/index",
        element: (
          <Top5DashboardIndexDataConsole />
        )
      },
      {
        path: "dashboard/exec/peer",
        element: (
          <Top5DashboardPeerDataConsole />
        )
      },
      {
        path: "dashboard/survey-output",
        element: (
          <SurveyOutputDashboardConsole />
        )
      },
      {
        path: "dashboard/revenue-regression",
        element: (
          <SurveyRegressionDashboardConsole regressionType={"revenue"} />
        )
      },
      {
        path: "dashboard/market-cap-regression",
        element: (
          <SurveyRegressionDashboardConsole regressionType={"market-cap"} />
        )
      },
      {
        path: "/external-data/options",
        element: (
          <GroupVisibleComponent groups={[USER_GROUPS.INTERNAL]}>
            <OptionsData />
          </GroupVisibleComponent>
        )
      },
      {
        path: "data-requests/create",
        element: (
          <GroupVisibleComponent groups={[USER_GROUPS.INTERNAL]}>
            <DataRequestSubmission />
          </GroupVisibleComponent>
        )
      },
      {
        path: "data-requests/user",
        element: (
          <GroupVisibleComponent groups={[USER_GROUPS.INTERNAL]}>
            <MyDataRequests />
          </GroupVisibleComponent>
        )
      },
      {
        path: "data-requests",
        element: (
          <GroupVisibleComponent groups={[USER_GROUPS.DATA_PROVIDER, USER_GROUPS.ADMIN]}>
            <AllDataRequests />
          </GroupVisibleComponent>
        )
      },
      {
        path: "download-history/:tabName",
        element: (
          <GroupVisibleComponent groups={[USER_GROUPS.INTERNAL, USER_GROUPS.CLIENT]}>
            <DownloadHistoryConsole />
          </GroupVisibleComponent>
        )
      },
      {
        path: "survey-data/console",
        element: (
          <GroupVisibleComponent groups={[USER_GROUPS.ADMIN, USER_GROUPS.INTERNAL, USER_GROUPS.CLIENT, USER_GROUPS.EXTERNAL]}>
            <SurveyDataConsole />
          </GroupVisibleComponent>
        )
      },
      {
        path: "survey-questionnaire-submission",
        element: (
          <GroupVisibleComponent groups={[USER_GROUPS.DATA_PROVIDER, USER_GROUPS.ADMIN]}>
            <SurveyQuestionnaireTrackingConsole />
          </GroupVisibleComponent>
        )
      },
      {
        path: "survey-questionnaire-submission/:companyId/:participantId",
        element: (
          <GroupVisibleComponent groups={[USER_GROUPS.DATA_PROVIDER, USER_GROUPS.ADMIN]}>
            <SurveyQuestionnaireDetailsConsole />
          </GroupVisibleComponent>
        ),
        loader: questionnaireDetailsLoader,
        errorElement: <ErrorBoundary />
      }
    ]
  },
  {
    path: "*",
    element: <NotFound />
  },
  {
    path: "/ecs-survey/:participantId",
    element: <SurveyParticipantConsole />
  }
]);

root.render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
